import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client'
import { isClientError, isUnauthorized } from '../lib/api'
import { recipeKeys } from './recipes'
import { sessionKeys } from './session'
import { taskKeys } from './tasks'

declare module '@tanstack/react-query' {
	interface Register {
		mutationMeta: { invalidates?: readonly QueryKey[] }
	}
}

const SESSION_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000
const MAX_NETWORK_RETRIES = 2
const BUILD_ID = String(import.meta.env.VITE_BUILD_ID ?? 'dev')
const RETIRED_CACHE_PREFIX = 'saysay-cache:'
const PERSISTED_KEYS: readonly QueryKey[] = [sessionKeys.all, recipeKeys.all, taskKeys.lists(), taskKeys.details()]

function startsWith(queryKey: QueryKey, prefix: QueryKey): boolean {
	return prefix.every((part, index) => queryKey[index] === part)
}

function isPersisted(queryKey: QueryKey): boolean {
	return PERSISTED_KEYS.some((prefix) => startsWith(queryKey, prefix))
}

function shouldRetry(failureCount: number, error: unknown): boolean {
	return !isClientError(error) && failureCount < MAX_NETWORK_RETRIES
}

function browserStorage(): Storage | null {
	try {
		return window.localStorage
	} catch {
		return null
	}
}

function removeRetiredCache(storage: Storage | null): void {
	if (!storage) return
	const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index) ?? '')
	for (const key of keys) if (key.startsWith(RETIRED_CACHE_PREFIX)) storage.removeItem(key)
}

function endSessionOn(error: unknown): void {
	if (isUnauthorized(error)) endSession()
}

const storage = browserStorage()
removeRetiredCache(storage)
const persister = createAsyncStoragePersister({ storage, key: 'saysay-query' })

// Trap: refetches after a save or on focus never raise isPending, only isFetching.
// A page shows its skeleton on isPending alone. Gating on isFetching unmounts the
// page on every background refresh and takes any open sheet or dialog with it.
export const queryClient = new QueryClient({
	queryCache: new QueryCache({ onError: endSessionOn }),
	mutationCache: new MutationCache({
		onError: endSessionOn,
		onSuccess: (_data, _variables, _context, mutation) => {
			for (const queryKey of mutation.meta?.invalidates ?? []) void queryClient.invalidateQueries({ queryKey })
		},
	}),
	defaultOptions: {
		queries: { staleTime: 30 * 1000, retry: shouldRetry },
	},
})

// Trap: gcTime runs through setTimeout, which fires at once above 2^31 ms (about 24.8 days).
// Thirty days dropped every persisted query the moment its page unmounted. Infinity never schedules removal.
for (const queryKey of PERSISTED_KEYS) queryClient.setQueryDefaults(queryKey, { gcTime: Infinity })

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
	persister,
	maxAge: SESSION_LIFETIME_MS,
	buster: BUILD_ID,
	dehydrateOptions: {
		shouldDehydrateQuery: (query) => query.state.status === 'success' && isPersisted(query.queryKey),
	},
}

export function endSession(): void {
	void persister.removeClient()
	queryClient.removeQueries({ predicate: (query) => !startsWith(query.queryKey, sessionKeys.all) })
	queryClient.setQueryData(sessionKeys.me(), null)
}
